import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Users } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import {
  formatDateTime,
  formatDurationLabel,
  formatInquiryDate,
  viewingStateLabel,
} from '../lib/bookingInquiryDisplay';
import { buildBookingsReturnUrl, getInquiryStatusLabel } from '../lib/bookingInquiryContext';

/**
 * Compact banner when listing detail is opened from /bookings with ?inquiry=.
 */
export default function BookingInquiryContextBanner({ inquiry, unavailable = false }) {
  const { t } = useLanguage();

  if (unavailable) {
    return (
      <div
        className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
        data-testid="booking-inquiry-context-unavailable"
      >
        {t('booking.context_unavailable', 'Booking context unavailable')}
      </div>
    );
  }

  if (!inquiry) {
    return null;
  }

  const checkIn = formatInquiryDate(inquiry.check_in_date)
    || t('booking.date_unavailable', 'Date unavailable');
  const checkOut = formatInquiryDate(inquiry.check_out_date)
    || t('booking.date_unavailable', 'Date unavailable');
  const created = formatDateTime(inquiry.created_at)
    || t('booking.not_yet_updated', 'Not yet updated');

  return (
    <div
      className="mb-6 rounded-lg border border-signal-blue/25 bg-signal-blue/5 px-4 py-4"
      data-testid="booking-inquiry-context-banner"
    >
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
        <div>
          <p className="text-sm font-semibold text-slate-900">
            {t('booking.viewing_from_inquiry', 'Viewing from inquiry')}
          </p>
          <p className="text-xs text-slate-600 mt-0.5 font-mono" title={inquiry.id}>
            {t('booking.inquiry_ref', 'Inquiry')}: {inquiry.inquiry_ref}
            {inquiry.listing_ref ? (
              <>
                {' '}
                · {t('booking.listing_ref', 'Listing')}: {inquiry.listing_ref}
              </>
            ) : null}
          </p>
        </div>
        <Link
          to={buildBookingsReturnUrl(inquiry.id)}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-signal-blue hover:text-signal-blue-hover shrink-0"
          data-testid="back-to-bookings-link"
        >
          <ArrowLeft className="w-4 h-4" aria-hidden />
          {t('booking.back_to_bookings', 'Back to My Bookings')}
        </Link>
      </div>

      <dl className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 text-xs">
        <div>
          <dt className="text-slate-500">{t('booking.check_in', 'Check-in')}</dt>
          <dd className="font-semibold text-slate-900 mt-0.5">{checkIn}</dd>
        </div>
        <div>
          <dt className="text-slate-500">{t('booking.check_out', 'Check-out')}</dt>
          <dd className="font-semibold text-slate-900 mt-0.5">{checkOut}</dd>
        </div>
        <div>
          <dt className="text-slate-500">{t('booking.duration', 'Duration')}</dt>
          <dd className="font-semibold text-slate-900 mt-0.5">{formatDurationLabel(inquiry, t)}</dd>
        </div>
        <div>
          <dt className="text-slate-500">{t('booking.guests_label', 'Guests')}</dt>
          <dd className="font-semibold text-slate-900 mt-0.5 flex items-center gap-1">
            <Users className="w-3.5 h-3.5 text-slate-500" aria-hidden />
            {inquiry.guests != null ? inquiry.guests : '—'}
          </dd>
        </div>
        <div>
          <dt className="text-slate-500">{t('booking.inquiry_status_label', 'Inquiry status')}</dt>
          <dd className="font-semibold text-slate-900 mt-0.5">{getInquiryStatusLabel(inquiry.status, t)}</dd>
        </div>
        <div>
          <dt className="text-slate-500">{t('booking.viewing_state_label', 'Viewing state')}</dt>
          <dd className="font-semibold text-slate-900 mt-0.5">
            {viewingStateLabel(inquiry.viewing_state, t)}
          </dd>
        </div>
      </dl>

      <p className="text-xs text-slate-500 mt-3 flex items-center gap-1.5">
        <Calendar className="w-3.5 h-3.5 shrink-0" aria-hidden />
        {t('booking.created_at', 'Created')}: <span className="text-slate-700">{created}</span>
      </p>
    </div>
  );
}
